import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthServiceTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  GROWTH_SUCCESS_PLAN_STATUSES,
  emptyGoalPayloadJson,
  emptyMilestonePayloadJson,
  growthSuccessGoalFields,
  growthSuccessMilestoneFields,
  parseJsonArrayPayloads,
  serializeJsonArrayPayloads
} from './growth-service-form.models';
import { GrowthServiceHealth, GrowthServiceService, GrowthSuccessPlan } from './growth-service.service';

type SuccessPlanTab = 'details' | 'health';

@Component({
  standalone: true,
  selector: 'ain-growth-success-plan-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    PartySelectorComponent,
    StructuredJsonFormComponent
  ],
  template: `
    <ain-page
      [title]="isCreateMode() ? t('growth.service.successPlanDetail.form.createTitle') : t('growth.service.successPlanDetail.form.editTitle')"
      [description]="t('growth.service.successPlanDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.service.successPlanDetail.help.title'"
        [descriptionKey]="'growth.service.successPlanDetail.help.description'"
        [bulletKeys]="['growth.service.successPlanDetail.help.bullets.goals', 'growth.service.successPlanDetail.help.bullets.health']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/growth/service/success-plans']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
        </div>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'details'" (click)="activeTab.set('details')">
              {{ t('growth.service.successPlanDetail.tabs.details') }}
            </button>
            @if (!isCreateMode() && form.partyId) {
              <button type="button" [class.active]="activeTab() === 'health'" (click)="switchToHealth()">
                {{ t('growth.service.successPlanDetail.tabs.health') }}
              </button>
            }
          </div>

          @if (activeTab() === 'details') {
            <div class="grid">
              @if (isCreateMode()) {
                <label class="wide">
                  <span>{{ t('growth.service.successPlanDetail.fields.partyId') }}</span>
                  <ain-party-selector [(ngModel)]="form.partyId" />
                </label>
              } @else {
                <label class="wide">
                  <span>{{ t('growth.service.successPlanDetail.fields.partyId') }}</span>
                  <ain-party-selector [ngModel]="form.partyId" [disabled]="true" />
                </label>
              }
              <label>
                <span>{{ t('growth.service.successPlanDetail.fields.name') }}</span>
                <input pInputText type="text" [(ngModel)]="form.name" />
              </label>
              @if (!isCreateMode()) {
                <label>
                  <span>{{ t('growth.service.successPlanDetail.fields.status') }}</span>
                  <p-select
                    class="field-select"
                    [options]="statusOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [(ngModel)]="form.status"
                    appendTo="body" />
                </label>
                <label>
                  <span>{{ t('growth.service.successPlanDetail.fields.healthScore') }}</span>
                  <input pInputText type="text" [ngModel]="plan()?.healthScore ?? '-'" [disabled]="true" />
                </label>
              }
            </div>

            <div class="list-section">
              <div class="list-section-head">
                <h4>{{ t('growth.service.successPlanDetail.goals.section') }}</h4>
                <button
                  pButton
                  type="button"
                  size="small"
                  severity="secondary"
                  [label]="t('growth.service.successPlanDetail.goals.add')"
                  (click)="addGoal()"></button>
              </div>
              <ain-info-hint
                [titleKey]="'growth.service.successPlanDetail.help.goals.title'"
                [descriptionKey]="'growth.service.successPlanDetail.help.goals.description'" />
              @if (goalPayloads.length === 0) {
                <p class="muted">{{ t('growth.service.successPlanDetail.goals.empty') }}</p>
              } @else {
                @for (payload of goalPayloads; track $index) {
                  <article class="item-card">
                    <div class="item-card-head">
                      <strong>{{ t('growth.service.successPlanDetail.goals.item') }} {{ $index + 1 }}</strong>
                      <button
                        pButton
                        type="button"
                        size="small"
                        severity="danger"
                        [text]="true"
                        [label]="t('growth.service.successPlanDetail.goals.remove')"
                        (click)="removeGoal($index)"></button>
                    </div>
                    <ain-structured-json-form [fields]="goalFields" [(payloadJson)]="goalPayloads[$index]" />
                  </article>
                }
              }
            </div>

            <div class="list-section">
              <div class="list-section-head">
                <h4>{{ t('growth.service.successPlanDetail.milestones.section') }}</h4>
                <button
                  pButton
                  type="button"
                  size="small"
                  severity="secondary"
                  [label]="t('growth.service.successPlanDetail.milestones.add')"
                  (click)="addMilestone()"></button>
              </div>
              <ain-info-hint
                [titleKey]="'growth.service.successPlanDetail.help.milestones.title'"
                [descriptionKey]="'growth.service.successPlanDetail.help.milestones.description'" />
              @if (milestonePayloads.length === 0) {
                <p class="muted">{{ t('growth.service.successPlanDetail.milestones.empty') }}</p>
              } @else {
                @for (payload of milestonePayloads; track $index) {
                  <article class="item-card">
                    <div class="item-card-head">
                      <strong>{{ t('growth.service.successPlanDetail.milestones.item') }} {{ $index + 1 }}</strong>
                      <button
                        pButton
                        type="button"
                        size="small"
                        severity="danger"
                        [text]="true"
                        [label]="t('growth.service.successPlanDetail.milestones.remove')"
                        (click)="removeMilestone($index)"></button>
                    </div>
                    <ain-structured-json-form [fields]="milestoneFields" [(payloadJson)]="milestonePayloads[$index]" />
                  </article>
                }
              }
            </div>
          }

          @if (activeTab() === 'health') {
            @if (healthLoading()) {
              <ain-state type="loading" [title]="t('common.loading')" />
            } @else if (health()) {
              <div class="grid">
                <label>
                  <span>{{ t('growth.service.successPlanDetail.health.healthScore') }}</span>
                  <input pInputText type="text" [ngModel]="health()!.healthScore ?? '-'" [disabled]="true" />
                </label>
                <label>
                  <span>{{ t('growth.service.successPlanDetail.health.clv') }}</span>
                  <input pInputText type="text" [ngModel]="health()!.clv ?? '-'" [disabled]="true" />
                </label>
                <label>
                  <span>{{ t('growth.service.successPlanDetail.health.churnRisk') }}</span>
                  <input pInputText type="text" [ngModel]="health()!.churnRisk ?? '-'" [disabled]="true" />
                </label>
                <label>
                  <span>{{ t('growth.service.successPlanDetail.health.churnRiskBand') }}</span>
                  <input pInputText type="text" [ngModel]="health()!.churnRiskBand ?? '-'" [disabled]="true" />
                </label>
                <label class="wide">
                  <span>{{ t('growth.service.successPlanDetail.health.disclaimer') }}</span>
                  <input pInputText type="text" [ngModel]="health()!.disclaimer" [disabled]="true" />
                </label>
                <label>
                  <span>{{ t('growth.service.successPlanDetail.health.openCaseLinks') }}</span>
                  <input pInputText type="text" [ngModel]="health()!.openCaseLinks.length" [disabled]="true" />
                </label>
                <label>
                  <span>{{ t('growth.service.successPlanDetail.health.openRetentionPlays') }}</span>
                  <input pInputText type="text" [ngModel]="health()!.openRetentionPlays.length" [disabled]="true" />
                </label>
              </div>
            } @else {
              <p class="muted">{{ t('growth.service.successPlanDetail.health.empty') }}</p>
            }
          }
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 12px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .wide { grid-column: span 3; }
    label { display: grid; gap: 6px; }
    .field-select { width: 100%; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .list-section { display: grid; gap: 10px; }
    .list-section-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .list-section-head h4 { margin: 0; }
    .item-card { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 8px; background: #f8fafc; }
    .item-card-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .message.error { color: #b91c1c; }
    .muted { color: #64748b; margin: 0; }
    @media (max-width: 1200px) {
      .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .wide { grid-column: span 2; }
    }
    @media (max-width: 760px) {
      .grid { grid-template-columns: 1fr; }
      .wide { grid-column: span 1; }
    }
  `]
})
export class GrowthSuccessPlanDetailComponent implements OnInit {
  private readonly api = inject(GrowthServiceService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly healthLoading = signal(false);
  readonly errorMessage = signal('');
  readonly isCreateMode = signal(false);
  readonly plan = signal<GrowthSuccessPlan | null>(null);
  readonly health = signal<GrowthServiceHealth | null>(null);
  readonly activeTab = signal<SuccessPlanTab>('details');

  readonly goalFields = growthSuccessGoalFields;
  readonly milestoneFields = growthSuccessMilestoneFields;

  goalPayloads: string[] = [];
  milestonePayloads: string[] = [];

  form = {
    partyId: null as string | null,
    name: '',
    status: 'Active'
  };

  private planId = '';
  private subscribedTopic: string | null = null;

  statusOptions(): Array<{ label: string; value: string }> {
    return GROWTH_SUCCESS_PLAN_STATUSES.map(value => ({
      value,
      label: this.t(`growth.service.successPlanDetail.status.${value}`)
    }));
  }

  ngOnInit(): void {
    this.planId = this.route.snapshot.paramMap.get('planId') ?? '';
    this.isCreateMode.set(!this.planId || this.planId === 'new');

    this.liveUpdateService.events$
      .pipe(
        filter(event => {
          if (event.eventType !== LiveUpdateEventTypes.growthServiceChanged) {
            return false;
          }
          if (this.isCreateMode()) {
            return false;
          }
          const payloadPlanId = event.payload['successPlanId'];
          return payloadPlanId === undefined || String(payloadPlanId).toLowerCase() === this.planId.toLowerCase();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();

    if (this.isCreateMode()) {
      this.goalPayloads = [emptyGoalPayloadJson()];
      this.milestonePayloads = [emptyMilestonePayloadJson()];
      this.loading.set(false);
      return;
    }
    this.load(true);
  }

  addGoal(): void {
    this.goalPayloads = [...this.goalPayloads, emptyGoalPayloadJson()];
  }

  removeGoal(index: number): void {
    this.goalPayloads = this.goalPayloads.filter((_, i) => i !== index);
  }

  addMilestone(): void {
    this.milestonePayloads = [...this.milestonePayloads, emptyMilestonePayloadJson()];
  }

  removeMilestone(index: number): void {
    this.milestonePayloads = this.milestonePayloads.filter((_, i) => i !== index);
  }

  switchToHealth(): void {
    this.activeTab.set('health');
    if (!this.form.partyId || this.health()) {
      return;
    }
    this.loadHealth();
  }

  load(showLoading = true): void {
    if (!this.planId || this.planId === 'new') {
      this.loading.set(false);
      return;
    }
    if (showLoading) {
      this.loading.set(true);
    }
    this.errorMessage.set('');
    this.api.getSuccessPlan(this.planId).subscribe({
      next: row => {
        this.plan.set(row);
        this.populateForm(row);
        this.loading.set(false);
      },
      error: () => {
        this.plan.set(null);
        this.loading.set(false);
        this.errorMessage.set(this.t('growth.service.successPlanDetail.messages.loadFailed'));
      }
    });
  }

  loadHealth(): void {
    if (!this.form.partyId) {
      return;
    }
    this.healthLoading.set(true);
    this.api.getHealth(this.form.partyId).subscribe({
      next: data => {
        this.health.set(data);
        this.healthLoading.set(false);
      },
      error: () => {
        this.health.set(null);
        this.healthLoading.set(false);
        this.errorMessage.set(this.t('growth.service.successPlanDetail.messages.healthFailed'));
      }
    });
  }

  save(): void {
    if (!this.form.name.trim()) {
      this.errorMessage.set(this.t('growth.service.successPlanDetail.messages.nameRequired'));
      return;
    }
    if (this.isCreateMode() && !this.form.partyId) {
      this.errorMessage.set(this.t('growth.service.successPlanDetail.messages.partyRequired'));
      return;
    }

    const goalsJson = serializeJsonArrayPayloads(this.goalPayloads);
    const milestonesJson = serializeJsonArrayPayloads(this.milestonePayloads);

    this.saving.set(true);
    this.errorMessage.set('');

    if (this.isCreateMode()) {
      this.api.createSuccessPlan({
        partyId: this.form.partyId!,
        name: this.form.name.trim(),
        goalsJson,
        milestonesJson
      }).subscribe({
        next: created => {
          this.saving.set(false);
          void this.router.navigate(['/growth/service/success-plans', created.id]);
        },
        error: () => {
          this.saving.set(false);
          this.errorMessage.set(this.t('growth.service.successPlanDetail.messages.saveFailed'));
        }
      });
      return;
    }

    this.api.updateSuccessPlan(this.planId, {
      name: this.form.name.trim(),
      status: this.form.status,
      goalsJson,
      milestonesJson
    }).subscribe({
      next: row => {
        this.plan.set(row);
        this.populateForm(row);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.errorMessage.set(this.t('growth.service.successPlanDetail.messages.saveFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private populateForm(row: GrowthSuccessPlan): void {
    this.form = {
      partyId: row.partyId,
      name: row.name,
      status: row.status
    };
    this.goalPayloads = parseJsonArrayPayloads(row.goalsJson, emptyGoalPayloadJson);
    this.milestonePayloads = parseJsonArrayPayloads(row.milestonesJson, emptyMilestonePayloadJson);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthServiceTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
